import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import { useCapability } from './useCapability';
import {
  computeUsable,
  parseGithubSkillUrl,
  rowToInstalledSkill,
  type InstalledSkill,
  type OrgSkillRow
} from '../models/skills';
import { captureSkillManifest } from '../skills/captureManifest';

export type SkillsRegistryStatus = 'loading' | 'unauth' | 'ready' | 'error';

const SKILL_COLUMNS =
  'id, org_id, source, name, repo_url, pinned_version, enabled, installed_by, created_at, updated_at, approval_status, approved_by, approved_at, requested_by, requested_at, manifest, storage_path, content_hash, file_manifest';

/**
 * Process-global monotonic counter making each realtime channel name unique per
 * effect-run (mirrors `useRealtimeRowReload`). `removeChannel` is async — it only
 * drops the channel after the server acks the UNSUBSCRIBE — so a remount that
 * re-subscribes before that ack would otherwise reuse the same topic and
 * `supabase.channel(topic)` would return the EXISTING subscribed channel, making
 * the follow-up `.on('postgres_changes', …)` throw. A per-mount nonce avoids it.
 */
let skillsChannelNonce = 0;

export interface InstallInput {
  url: string;
  /** Optional display-name override; defaults to the repo segment. */
  name?: string;
  /** Optional pinned ref; blank installs the skill unpinned. */
  version?: string;
}

/** One file in a user-authored skill (Skills 1.9 — create-skill, Door A/B). */
export interface CreateSkillFile {
  /** Skill-relative path (SKILL.md at the top). */
  path: string;
  /** UTF-8 text content. */
  content: string;
}

/** Input for {@link UseSkillsRegistryVM.actions.createSkill}. */
export interface CreateSkillInput {
  name: string;
  files: CreateSkillFile[];
  /** SKILL.md frontmatter manifest (Skills 1.5), parsed client-side; optional. */
  manifest?: unknown;
}

export interface UseSkillsRegistryVM {
  status: SkillsRegistryStatus;
  orgId: string | null;
  orgName: string | null;
  /** Org allowlist posture — the master switch (Skills 1.2). */
  allowlistEnabled: boolean;
  /**
   * Whether the current viewer may install / toggle / uninstall skills.
   * Resolves from the active membership's `skills.install` capability (slice 1.3).
   */
  canInstall: boolean;
  /**
   * Whether the current viewer may curate (approve/revoke/block, flip the
   * posture, review requests). Resolves from the `skills.manage_allowlist`
   * capability (slice 1.3 — was the member-permitted `canCurate` seam in 1.1/1.2).
   */
  canCurate: boolean;
  /** True while the capability checks are still resolving — UI should avoid
   *  flashing a "blocked by policy" reason until caps are known. */
  capsLoading: boolean;
  /** Installed skills (everything that isn't a pending member request). */
  installed: InstalledSkill[];
  /** Member-requested, not-yet-installed skills (Requested review queue). */
  requested: InstalledSkill[];
  error: string | null;
  installError: string | null;
  installing: boolean;
  togglingIds: ReadonlySet<string>;
  curatingIds: ReadonlySet<string>;
  postureSaving: boolean;
  toast: string | null;
  /** Effective governed `usable` for a skill, given the current posture. */
  usable: (s: InstalledSkill) => boolean;
  actions: {
    /**
     * Add a skill, branching on posture + canCurate:
     * off → install (usable now); on + canCurate → install & approve;
     * on + member → request (lands in the Requested queue).
     */
    add: (input: InstallInput) => Promise<boolean>;
    /**
     * Create a user-authored skill (Skills 1.9, Door A folder import). POSTs the
     * file set to the `store-skill-files` `mode:'create'` path, which stores the
     * files + inserts a `source='custom'` row, then reconciles it into local
     * state so it appears in the palette immediately (pending under an enabled
     * allowlist; auto-approved when a curator creates under one).
     */
    createSkill: (input: CreateSkillInput) => Promise<boolean>;
    toggle: (id: string, enabled: boolean) => Promise<void>;
    uninstall: (id: string) => Promise<void>;
    approve: (id: string) => Promise<void>;
    /** Block / revoke approval (both set approval_status = 'blocked'). */
    revoke: (id: string) => Promise<void>;
    block: (id: string) => Promise<void>;
    setAllowlistEnabled: (enabled: boolean) => Promise<void>;
    /** Approve a member request (moves it into Installed as approved). */
    approveRequest: (id: string) => Promise<void>;
    /** Deny a member request (discards the requested row). */
    denyRequest: (id: string) => Promise<void>;
    clearInstallError: () => void;
    clearToast: () => void;
  };
}

type SkillEnvelope =
  | { ok: true; skill: OrgSkillRow }
  | { ok: true; id: string }
  | { ok: false; error: string };

type PolicyEnvelope =
  | { ok: true; policy: { allowlist_enabled: boolean } }
  | { ok: false; error: string };

/**
 * Envelope from the `store-skill-files` Edge Function (Skills 1.7). It fetches +
 * validates + stores the skill's files server-side, then (install mode) commits
 * the row. Always HTTP 200, so we read `data.ok`/`data.error` directly.
 */
type StoreFnEnvelope =
  | { ok: true; skill: OrgSkillRow }
  | { ok: false; error: string };

/** Suffix shown on a fetch failure — manual add lands in Skills 1.9 (create-skill). */
const MANUAL_ADD_HINT = ' You can clone the repo and add it manually.';

function installErrorCopy(code: string): string {
  switch (code) {
    case 'already_installed': return 'That skill is already installed in this org.';
    case 'unsupported_source': return 'Only GitHub installs are supported right now.';
    case 'invalid_input': return 'Enter a skill name.';
    case 'forbidden': return "You don't have access to install in this org.";
    // Skills 1.7 — server-side fetch/validation outcomes from store-skill-files.
    case 'no_skill_md': return 'No skill found in that repo — it has no SKILL.md.' + MANUAL_ADD_HINT;
    case 'ambiguous_monorepo':
      return 'That repo holds multiple skills. Install one by name — set the skill name to the folder you want (e.g. “frontend-design”).';
    case 'too_large': return 'That skill is too large to import.' + MANUAL_ADD_HINT;
    case 'disallowed_type': return "That repo has files we can't import." + MANUAL_ADD_HINT;
    case 'unreachable': return "Couldn't reach that repo." + MANUAL_ADD_HINT;
    default: return "Couldn't install the skill. Try again.";
  }
}

/** Plain-language copy for a `mode:'create'` failure (Skills 1.9). Distinct from
 *  install copy — the manual-add hint doesn't apply to a folder/agent author. */
function createErrorCopy(code: string): string {
  switch (code) {
    case 'already_installed': return 'A skill with that name already exists in this org.';
    case 'no_skill_md': return 'The folder needs a SKILL.md at its top level.';
    case 'too_large': return 'That skill is too large — trim the folder and try again.';
    case 'disallowed_type': return "That folder has files we can't store.";
    case 'invalid_input': return 'Enter a name and pick a folder with a SKILL.md.';
    case 'forbidden': return "You don't have permission to create skills in this org.";
    default: return "Couldn't create the skill. Try again.";
  }
}

/**
 * Resolve an error code from either the app-level envelope (`{ ok:false, error }`)
 * or a PostgREST error. A DB-raised `forbidden` (errcode 42501 — the slice-1.3
 * capability gate) surfaces as an HTTP error with `code === '42501'`, not an
 * envelope, so map it explicitly so the access reason isn't masked as generic.
 */
function rpcErrorCode(
  rpcErr: { code?: string } | null,
  env: { ok: false; error: string } | null
): string {
  if (env && env.ok === false) return env.error;
  if (rpcErr?.code === '42501') return 'forbidden';
  return 'unknown';
}

export function useSkillsRegistryVM(): UseSkillsRegistryVM {
  const { activeOrgId: orgId, activeOrgName: orgName } = useActiveOrg();
  const [status, setStatus] = useState<SkillsRegistryStatus>('loading');
  const [allSkills, setAllSkills] = useState<InstalledSkill[]>([]);
  const [allowlistEnabled, setAllowlistEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [curatingIds, setCuratingIds] = useState<Set<string>>(new Set());
  const [postureSaving, setPostureSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Capability gates (slice 1.3). Resolved from the active membership's
  // permission-group capability_keys — no extra round-trip (useShellSession
  // already joins them). The DB RPCs are the authoritative gate; these drive
  // the visible disabled-with-reason UI. Owner holds both caps via the backfill
  // + dynamic handle_new_user seed, so owners resolve true here.
  const installCap = useCapability(orgId, 'skills.install');
  const curateCap = useCapability(orgId, 'skills.manage_allowlist');
  const canInstall = installCap.allowed;
  const canCurate = curateCap.allowed;
  const capsLoading = installCap.loading || curateCap.loading;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessionData.session) {
        setStatus('unauth');
        return;
      }
      if (!orgId) return;

      // Skills + posture in one round-trip's worth of latency (parallel).
      const [skillsRes, policyRes] = await Promise.all([
        supabase
          .from('org_skills')
          .select(SKILL_COLUMNS)
          .eq('org_id', orgId)
          .order('created_at', { ascending: false }),
        supabase
          .from('org_skill_policy')
          .select('allowlist_enabled')
          .eq('org_id', orgId)
          .maybeSingle()
      ]);

      if (cancelled) return;
      if (skillsRes.error) {
        setStatus('error');
        setError("Couldn't load skills. Try again.");
        return;
      }
      setAllSkills(((skillsRes.data ?? []) as OrgSkillRow[]).map(rowToInstalledSkill));
      // A missing policy row means the allowlist is off (permissive default).
      setAllowlistEnabled(Boolean(policyRes.data?.allowlist_enabled));
      setError(null);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Skills 1.9 — live reconcile. Subscribe to org_skills changes for the active
  // org so the registry (the slash launcher + Settings) refreshes on ANY external
  // write — notably the agent's `save_skill` (create-skill Door B), which persists
  // server-side and would otherwise not appear until the next load. RLS gates
  // delivery (the member-select policy on org_skills), and a debounce coalesces
  // write bursts into a single refetch. Door A's in-VM reconcile still applies;
  // this is the backstop for writes that DON'T flow through this hook.
  useEffect(() => {
    if (!orgId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reconcile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        timer = null;
        const { data, error: reErr } = await supabase
          .from('org_skills')
          .select(SKILL_COLUMNS)
          .eq('org_id', orgId)
          .order('created_at', { ascending: false });
        if (!reErr) {
          setAllSkills(((data ?? []) as OrgSkillRow[]).map(rowToInstalledSkill));
        }
      }, 250);
    };
    const channel = supabase
      .channel(`org-skills:${orgId}:${++skillsChannelNonce}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'org_skills', filter: `org_id=eq.${orgId}` },
        reconcile
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [orgId]);

  const clearInstallError = useCallback(() => setInstallError(null), []);
  const clearToast = useCallback(() => setToast(null), []);

  const usable = useCallback(
    (s: InstalledSkill) => computeUsable(allowlistEnabled, s.approvalStatus),
    [allowlistEnabled]
  );

  const installed = useMemo(
    () => allSkills.filter((s) => s.approvalStatus !== 'requested'),
    [allSkills]
  );
  const requested = useMemo(
    () => allSkills.filter((s) => s.approvalStatus === 'requested'),
    [allSkills]
  );

  /** Call set_skill_approval and reconcile the row in local state. */
  const setApproval = useCallback(
    async (id: string, statusValue: 'approved' | 'blocked', okToast: string) => {
      setCuratingIds((cur) => new Set(cur).add(id));
      const { data, error: rpcErr } = await supabase.rpc('set_skill_approval', {
        p_skill_id: id,
        p_status: statusValue
      });
      setCuratingIds((cur) => {
        const next = new Set(cur);
        next.delete(id);
        return next;
      });

      const env = data as SkillEnvelope | null;
      if (rpcErr || !env || env.ok !== true || !('skill' in env)) {
        setToast("Couldn't update approval. Try again.");
        return;
      }
      const updated = rowToInstalledSkill(env.skill);
      setAllSkills((cur) => cur.map((s) => (s.id === id ? updated : s)));
      setToast(okToast);
    },
    []
  );

  const approve = useCallback(
    (id: string) => {
      const name = allSkills.find((s) => s.id === id)?.name ?? 'Skill';
      return setApproval(id, 'approved', `${name} approved — usable by every member.`);
    },
    [allSkills, setApproval]
  );
  const block = useCallback(
    (id: string) => {
      const name = allSkills.find((s) => s.id === id)?.name ?? 'Skill';
      return setApproval(id, 'blocked', `${name} blocked by org policy.`);
    },
    [allSkills, setApproval]
  );
  // Revoke is "block an already-approved skill" — same underlying state.
  const revoke = block;

  const approveRequest = useCallback(
    async (id: string) => {
      const sk = allSkills.find((s) => s.id === id);
      const name = sk?.name ?? 'Skill';
      // Skills 1.7 — a requested skill has no files yet. Materialize them BEFORE
      // approving, so an approved (usable) skill is never file-less. If the fetch
      // fails, surface why and leave it in the Requested queue (not approved).
      if (sk && sk.source === 'github' && !sk.stored) {
        setCuratingIds((cur) => new Set(cur).add(id));
        const { data, error: fnErr } = await supabase.functions.invoke('store-skill-files', {
          body: { mode: 'materialize', skillId: id }
        });
        const env = data as StoreFnEnvelope | null;
        if (fnErr || !env || env.ok !== true) {
          setCuratingIds((cur) => {
            const next = new Set(cur);
            next.delete(id);
            return next;
          });
          setToast(installErrorCopy(env && env.ok === false ? env.error : 'unknown'));
          return;
        }
      }
      await setApproval(id, 'approved', `${name} approved & added — usable by every member.`);
    },
    [allSkills, setApproval]
  );

  const setAllowlistEnabledAction = useCallback(
    async (enabled: boolean) => {
      if (!orgId) return;
      const prev = allowlistEnabled;
      setPostureSaving(true);
      setAllowlistEnabled(enabled); // optimistic
      const { data, error: rpcErr } = await supabase.rpc('set_org_allowlist_enabled', {
        p_org_id: orgId,
        p_enabled: enabled
      });
      setPostureSaving(false);

      const env = data as PolicyEnvelope | null;
      if (rpcErr || !env || env.ok !== true) {
        setAllowlistEnabled(prev);
        setToast("Couldn't update the allowlist. Try again.");
        return;
      }
      setToast(
        enabled
          ? 'Allowlist on — only approved skills are usable.'
          : 'Allowlist off — every installed skill is usable again.'
      );
    },
    [orgId, allowlistEnabled]
  );

  const add = useCallback(
    async (input: InstallInput) => {
      if (!orgId) return false;
      setInstallError(null);
      const parsed = parseGithubSkillUrl(input.url);
      if (!parsed.ok) {
        setInstallError('Enter a valid GitHub repo URL (github.com/owner/repo).');
        return false;
      }
      const name = (input.name?.trim() || parsed.name).trim();
      const version = input.version?.trim() || null;
      if (!name) {
        setInstallError('Enter a skill name.');
        return false;
      }

      // Member request path (allowlist on, not a curator): no install yet.
      if (allowlistEnabled && !canCurate) {
        setInstalling(true);
        const { data, error: rpcErr } = await supabase.rpc('request_skill', {
          p_org_id: orgId,
          p_repo_url: parsed.repoUrl,
          p_name: name
        });
        setInstalling(false);
        const env = data as SkillEnvelope | null;
        if (rpcErr || !env || env.ok !== true || !('skill' in env)) {
          setInstallError(
            installErrorCopy(rpcErrorCode(rpcErr, env && env.ok === false ? env : null))
          );
          return false;
        }
        const requestedRow = rowToInstalledSkill(env.skill);
        setAllSkills((cur) => [requestedRow, ...cur.filter((s) => s.id !== requestedRow.id)]);
        setToast(`${requestedRow.name} requested — awaiting admin review.`);
        return true;
      }

      // Install path (allowlist off, or curator adding). Skills 1.7 — the install
      // now fetches the skill's actual files server-side and only commits the row
      // once they're stored (store-then-insert via the store-skill-files Edge
      // Function). `installing` stays true across the whole fetch+store so the UI
      // waits-and-confirms; on any failure NOTHING is added (no orphan row, no
      // partial files) and a plain-language reason is surfaced.
      setInstalling(true);
      // Skills 1.5 — best-effort capture of the SKILL.md frontmatter manifest at
      // the pinned ref so the curated form can render declared parameters. NEVER
      // blocks install: any failure → null → the skill degrades to instant-insert.
      const manifest = await captureSkillManifest({
        owner: parsed.owner,
        repo: parsed.repo,
        name,
        ref: version
      });
      const { data, error: fnErr } = await supabase.functions.invoke('store-skill-files', {
        body: {
          mode: 'install',
          orgId,
          name,
          repoUrl: parsed.repoUrl,
          pinnedVersion: version,
          manifest
        }
      });

      const env = data as StoreFnEnvelope | null;
      if (fnErr || !env || env.ok !== true || !('skill' in env)) {
        setInstalling(false);
        setInstallError(installErrorCopy(env && env.ok === false ? env.error : 'unknown'));
        return false;
      }
      let added = rowToInstalledSkill(env.skill);

      // Curator adding under an enabled allowlist: add AND approve in one gesture.
      if (allowlistEnabled && canCurate) {
        const { data: aData } = await supabase.rpc('set_skill_approval', {
          p_skill_id: added.id,
          p_status: 'approved'
        });
        const aEnv = aData as SkillEnvelope | null;
        if (aEnv && aEnv.ok === true && 'skill' in aEnv) {
          added = rowToInstalledSkill(aEnv.skill);
        }
      }
      setInstalling(false);
      setAllSkills((cur) => [added, ...cur.filter((s) => s.id !== added.id)]);
      setToast(
        allowlistEnabled && canCurate
          ? `${added.name} added & approved — usable by all members.`
          : `Installed ${added.name}.`
      );
      return true;
    },
    [orgId, allowlistEnabled, canCurate]
  );

  const createSkill = useCallback(
    async (input: CreateSkillInput) => {
      if (!orgId) return false;
      setInstallError(null);
      const name = input.name.trim();
      if (!name) {
        setInstallError('Enter a skill name.');
        return false;
      }
      if (!input.files.length) {
        setInstallError('Pick a folder with a SKILL.md.');
        return false;
      }

      // Skills 1.9 — one store path: the create door writes through the same
      // store-skill-files Edge Function as the GitHub install (mode:'create'),
      // which stores the files + inserts a source='custom' row. `installing`
      // stays true across the store so the dialog waits-and-confirms; on any
      // failure NOTHING is added and a plain-language reason is surfaced.
      setInstalling(true);
      const { data, error: fnErr } = await supabase.functions.invoke('store-skill-files', {
        body: {
          mode: 'create',
          orgId,
          name,
          files: input.files,
          manifest: input.manifest ?? null
        }
      });

      const env = data as StoreFnEnvelope | null;
      if (fnErr || !env || env.ok !== true || !('skill' in env)) {
        setInstalling(false);
        setInstallError(createErrorCopy(env && env.ok === false ? env.error : 'unknown'));
        return false;
      }
      let added = rowToInstalledSkill(env.skill);

      // Curator creating under an enabled allowlist: create AND approve in one
      // gesture (mirrors the install path), so a curator's own skill is usable now.
      if (allowlistEnabled && canCurate) {
        const { data: aData } = await supabase.rpc('set_skill_approval', {
          p_skill_id: added.id,
          p_status: 'approved'
        });
        const aEnv = aData as SkillEnvelope | null;
        if (aEnv && aEnv.ok === true && 'skill' in aEnv) {
          added = rowToInstalledSkill(aEnv.skill);
        }
      }
      setInstalling(false);
      setAllSkills((cur) => [added, ...cur.filter((s) => s.id !== added.id)]);
      setToast(
        allowlistEnabled && canCurate
          ? `${added.name} created & approved — usable by all members.`
          : `Created ${added.name}.`
      );
      return true;
    },
    [orgId, allowlistEnabled, canCurate]
  );

  const toggle = useCallback(
    async (id: string, enabled: boolean) => {
      const prev = allSkills;
      setTogglingIds((cur) => new Set(cur).add(id));
      setAllSkills((cur) => cur.map((s) => (s.id === id ? { ...s, enabled } : s)));

      const { data, error: rpcErr } = await supabase.rpc('set_skill_enabled', {
        p_skill_id: id,
        p_enabled: enabled
      });
      setTogglingIds((cur) => {
        const next = new Set(cur);
        next.delete(id);
        return next;
      });

      const env = data as SkillEnvelope | null;
      if (rpcErr || !env || env.ok !== true) {
        setAllSkills(prev);
        setToast("Couldn't update the skill. Try again.");
      }
    },
    [allSkills]
  );

  const uninstall = useCallback(
    async (id: string) => {
      const prev = allSkills;
      setAllSkills((cur) => cur.filter((s) => s.id !== id));
      const { data, error: rpcErr } = await supabase.rpc('uninstall_skill', {
        p_skill_id: id
      });
      const env = data as SkillEnvelope | null;
      if (rpcErr || !env || env.ok !== true) {
        setAllSkills(prev);
        setToast("Couldn't remove the skill. Try again.");
        return;
      }
      setToast('Skill removed.');
    },
    [allSkills]
  );

  const denyRequest = useCallback(
    async (id: string) => {
      const name = allSkills.find((s) => s.id === id)?.name ?? 'Request';
      const prev = allSkills;
      setAllSkills((cur) => cur.filter((s) => s.id !== id));
      const { data, error: rpcErr } = await supabase.rpc('uninstall_skill', {
        p_skill_id: id
      });
      const env = data as SkillEnvelope | null;
      if (rpcErr || !env || env.ok !== true) {
        setAllSkills(prev);
        setToast("Couldn't deny the request. Try again.");
        return;
      }
      setToast(`${name} request denied.`);
    },
    [allSkills]
  );

  return {
    status,
    orgId,
    orgName,
    allowlistEnabled,
    canInstall,
    canCurate,
    capsLoading,
    installed,
    requested,
    error,
    installError,
    installing,
    togglingIds,
    curatingIds,
    postureSaving,
    toast,
    usable,
    actions: {
      add,
      createSkill,
      toggle,
      uninstall,
      approve,
      revoke,
      block,
      setAllowlistEnabled: setAllowlistEnabledAction,
      approveRequest,
      denyRequest,
      clearInstallError,
      clearToast
    }
  };
}
